import { Button, Card, Fade, Grid, Modal } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import CustomTextField from "components/Form/CustomTextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import invoiceCustomerSchema from "./invoiceCustomerDataSchema";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const EditInvoiceCustomerDataModal = ({ open, setOpen, onSubmit, customerInformation, type }) => {
  const classes = useStyle();

  const resolver = useYupValidationResolver(invoiceCustomerSchema);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    getValues,
    formState: { dirtyFields, errors },
  } = useForm({
    resolver,
    defaultValues: {
      // Invoice Information
      invoiceNumber: customerInformation?.invoiceNumber ?? "1",
      customerNumber: customerInformation?.customerNumber ?? "",
      purchaseOrder: customerInformation?.purchaseOrder ?? "",
      date: customerInformation?.date ?? "",
      dueDate: customerInformation?.dueDate ?? "",
      costDeptCode: customerInformation?.costDeptCode ?? "",
      // Customer/Remittance Information
      name: customerInformation?.name ?? "",
      address: customerInformation?.address ?? "",
      city: customerInformation?.city ?? "",
      state: customerInformation?.state ?? "",
      zip: customerInformation?.zip ?? "",
      attn: customerInformation?.attn ?? "",
      // Remittance Information
      emailCopy: customerInformation?.emailCopy ?? "",
      notes: customerInformation?.notes ?? "",
    },
  });

  const cleanInput = (value) => {
    // Remove any character that's not a digit or decimal point
    const cleaned = value.replace(/[^\d]/g, "");

    return cleaned;
  };

  const handleSubmitFunc = (values) => {
    onSubmit(values);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={open}>
        <Card className={[classes.container].join(" ")}>
          <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }} key="text_addNote">
            {type === "remittance" ? "Edit Remittance Information" : "Edit Customer Information"}
          </MDTypography>
          <MDBox p={3}>
            <form onSubmit={handleSubmit(handleSubmitFunc)}>
              <Grid container xs={12} rowSpacing={3} columnSpacing={2}>
                {type === "invoice" && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="invoiceNumber"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("invoiceNumber", cleanInput(e.target.value ?? ""));
                            }}
                            label="Invoice Number"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="customerNumber"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("customerNumber", e.target.value ?? "");
                            }}
                            label="Customer Number"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                            disabled
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="purchaseOrder"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("purchaseOrder", e.target.value ?? "");
                            }}
                            label="Purchase Order"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="date"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                              {...field}
                              value={
                                field.value
                                  ? moment(field.value, "MM/DD/YY", true).toISOString()
                                  : ""
                              }
                              label="Invoice Date"
                              renderInput={(params) => <CustomTextField {...params} />}
                              onChange={(date) => {
                                if (date.isValid()) {
                                  setValue("date", date.format("MM/DD/YY"));
                                  return;
                                }
                                setValue("date", "");
                              }}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="dueDate"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                              {...field}
                              value={
                                field.value
                                  ? moment(field.value, "MM/DD/YY", true).toISOString()
                                  : ""
                              }
                              label="Due Date"
                              renderInput={(params) => <CustomTextField {...params} />}
                              onChange={(date) => {
                                if (date.isValid()) {
                                  setValue("dueDate", date.format("MM/DD/YY"));
                                  return;
                                }
                                setValue("dueDate", "");
                              }}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="costDeptCode"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("costDeptCode", e.target.value ?? "");
                            }}
                            label="Cost/Dept. Code"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {(type === "remittance" || type === "customer") && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("name", e.target.value ?? "");
                            }}
                            label="Company Name"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="address"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("address", e.target.value ?? "");
                            }}
                            label="Address"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="city"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("city", e.target.value ?? "");
                            }}
                            label="City"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="state"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("state", e.target.value ?? "");
                            }}
                            label="State"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="zip"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("zip", e.target.value ?? "");
                            }}
                            label="Zip Code"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="attn"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("attn", e.target.value ?? "");
                            }}
                            label="Attention"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {type === "remittance" && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="emailCopy"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("emailCopy", e.target.value ?? "");
                            }}
                            label="Email Copy"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="notes"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CustomTextField
                            {...field}
                            onChange={(e) => {
                              setValue("notes", e.target.value ?? "");
                            }}
                            label="Notes"
                            InputLabelProps={{ shrink: true }}
                            showError
                            errorMessage={error?.message}
                            multiline
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid container mt={2} justifyContent="flex-end">
                <Grid container item width="auto" spacing={3} justifyContent="flex-end">
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      variant="contained"
                      fullWidth
                      onClick={async (e) => {
                        e?.stopPropagation();
                        setOpen(false);
                      }}
                    >
                      <MDTypography variant="body2" color="white">
                        Cancel
                      </MDTypography>
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button type="submit" variant="contained" fullWidth>
                      <MDTypography variant="body2" color="white">
                        Save
                      </MDTypography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
};

export default EditInvoiceCustomerDataModal;
